import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';
import TenureBadge from './TenureBadge';
import { formatDate } from '../lib/dateUtils';
import {
  Award, Plus, Pencil, Trash2, Users, Search, X, Upload,
  Star, Trophy, Medal, Crown, Heart, ThumbsUp, Zap, Flame,
  Target, Gem, Sparkles, Check, Eye, UserPlus, XCircle,
  ChevronDown, Loader2, Clock, Briefcase, Shield, Info, MoreVertical,
  Image as ImageIcon, Settings, ToggleLeft, ToggleRight
} from 'lucide-react';

const BACKEND_URL = '';

// Helper to resolve image URLs - uses relative URLs to stay on same domain
// IMPORTANT: /uploads paths need to be /api/uploads for proper routing through Kubernetes ingress
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    // Convert /uploads/ to /api/uploads/ (relative URL)
    return `/api${url}`;
  }
  if (url.startsWith('/api/uploads/')) {
    // Already correct format (relative URL)
    return url;
  }
  if (url.startsWith('/')) {
    // Other relative paths - keep as-is
    return url;
  }
  return url; // Absolute URL, use as-is
};

// Icon mapping
const ICON_MAP = {
  star: Star,
  trophy: Trophy,
  medal: Medal,
  award: Award,
  crown: Crown,
  heart: Heart,
  'thumbs-up': ThumbsUp,
  zap: Zap,
  flame: Flame,
  target: Target,
  gem: Gem,
  sparkles: Sparkles,
  shield: Shield,
  briefcase: Briefcase,
};

// Badge Preview Component
const BadgePreview = ({ badge, size = 'medium' }) => {
  const sizes = {
    small: { container: 'w-8 h-8', icon: 14 },
    medium: { container: 'w-10 h-10', icon: 16 },
    large: { container: 'w-14 h-14', icon: 20 },
  };
  
  const s = sizes[size] || sizes.medium;
  const Icon = ICON_MAP[badge.icon_type] || Star;
  const imageUrl = resolveImageUrl(badge.image_url);
  
  if (imageUrl) {
    return (
      <div 
        className={`${s.container} rounded-full overflow-hidden border-2 shadow-soft flex-shrink-0`}
        style={{ borderColor: badge.color }}
      >
        <img src={imageUrl} alt={badge.name} className="w-full h-full object-cover" />
      </div>
    );
  }
  
  return (
    <div 
      className={`${s.container} rounded-full flex items-center justify-center shadow-soft flex-shrink-0`}
      style={{ backgroundColor: badge.color }}
    >
      <Icon size={s.icon} className="text-white" />
    </div>
  );
};

// Designation Badge Preview (editable)
const DesignationBadgePreview = ({ badge, onEdit }) => {
  const Icon = ICON_MAP[badge.icon_type] || Star;
  const imageUrl = resolveImageUrl(badge.image_url);
  
  return (
    <div 
      className="flex flex-col items-center group cursor-pointer"
      onClick={() => onEdit(badge)}
      title="Click to edit"
    >
      <div className="relative">
        {imageUrl ? (
          <div 
            className="w-10 h-10 rounded-full overflow-hidden border-2 shadow-soft transition-transform group-hover:scale-110"
            style={{ borderColor: badge.color }}
          >
            <img src={imageUrl} alt={badge.name} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center shadow-soft transition-transform group-hover:scale-110"
            style={{ backgroundColor: badge.color }}
          >
            <Icon size={16} className="text-white" />
          </div>
        )}
        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full border border-white/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Pencil className="w-2.5 h-2.5 text-neutral-500" />
        </div>
      </div>
      <span className="text-xs text-neutral-600 mt-1 text-center font-medium">{badge.name}</span>
    </div>
  );
};

// Create/Edit Badge Modal
function BadgeFormModal({ isOpen, onClose, badge, metadata, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon_type: 'star',
    color: '#6366F1',
    category: 'recognition',
    image_data: null,
    image_type: null,
    show_border: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    if (isOpen) {
      if (badge) {
        setFormData({
          name: badge.name || '',
          description: badge.description || '',
          icon_type: badge.icon_type || 'star',
          color: badge.color || '#6366F1',
          category: badge.category || 'recognition',
          image_data: null,
          image_type: null,
          show_border: badge.show_border !== false, // Default to true if not set
        });
        setImagePreview(badge.image_url || null);
      } else {
        setFormData({
          name: '',
          description: '',
          icon_type: 'star',
          color: '#6366F1',
          category: 'recognition',
          image_data: null,
          image_type: null,
          show_border: true,
        });
        setImagePreview(null);
      }
    }
  }, [badge, isOpen]);
  
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setFormData(prev => ({ ...prev, image_data: base64, image_type: file.type }));
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_data: null, image_type: null }));
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Badge name is required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };
  
  const Icon = ICON_MAP[formData.icon_type] || Star;
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col" data-testid="badge-form-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/40">
          <h2 className="text-lg font-semibold text-[#1a1a2e]">
            {badge ? 'Edit Badge' : 'Create Badge'}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Live Preview */}
            <div className="flex justify-center py-4 bg-white/40 rounded-xl">
              <div className="text-center">
                {imagePreview ? (
                  <div 
                    className={`w-14 h-14 rounded-full overflow-hidden mx-auto shadow ${formData.show_border ? 'border-2' : ''}`}
                    style={{ borderColor: formData.show_border ? formData.color : 'transparent' }}
                  >
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto shadow"
                    style={{ backgroundColor: formData.color }}
                  >
                    <Icon size={24} className="text-white" />
                  </div>
                )}
                <p className="mt-2 font-medium text-[#1a1a2e] text-sm">{formData.name || 'Badge Name'}</p>
              </div>
            </div>
            
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Employee of the Month"
                className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                data-testid="badge-name-input"
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What this badge recognizes..."
                rows={2}
                className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              />
            </div>
            
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Custom Image</label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="flex-1 px-2 py-1.5 border border-white/40 rounded-xl text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700"
                />
                {imagePreview && (
                  <button type="button" onClick={handleRemoveImage} className="p-1.5 text-neutral-400 hover:text-red-500 rounded">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-neutral-400 mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Recommended: Square image (1:1), PNG/JPG, max 1MB. Auto-resized to 256×256px.
              </p>
            </div>
            
            {/* Icon Selection */}
            {!imagePreview && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Icon</label>
                <div className="grid grid-cols-6 gap-1.5">
                  {metadata?.icons?.map((icon) => {
                    const IconComponent = ICON_MAP[icon.value] || Star;
                    const isSelected = formData.icon_type === icon.value;
                    return (
                      <button
                        key={icon.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon_type: icon.value }))}
                        className={`p-2 rounded-xl border transition-all ${
                          isSelected ? 'border-teal-500 bg-teal-50' : 'border-white/40 hover:border-white/40'
                        }`}
                        title={icon.label}
                      >
                        <IconComponent className="w-4 h-4 mx-auto" style={{ color: isSelected ? '#14B8A6' : formData.color }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Color Selection */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Color</label>
              <div className="flex items-center gap-2">
                <div className="flex gap-1 flex-wrap">
                  {metadata?.suggested_colors?.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${
                        formData.color === color.value ? 'border-gray-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-8 h-6 rounded cursor-pointer border border-white/40"
                />
              </div>
            </div>
            
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {metadata?.categories?.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            
            {/* Show Border Toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <label className="block text-sm font-medium text-neutral-700">Show Circle Border</label>
                <p className="text-xs text-neutral-500">Display a colored border around the badge</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, show_border: !prev.show_border }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.show_border ? 'bg-teal-600' : 'bg-gray-300'
                }`}
                data-testid="badge-border-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                    formData.show_border ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </form>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/40 bg-white/40">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 text-sm text-neutral-700 font-medium rounded-xl hover:bg-neutral-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="badge-save-btn"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            {badge ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Designation Badge Modal
function EditDesignationBadgeModal({ isOpen, onClose, badge, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    color: '',
    icon_type: '',
    description: '',
    image_data: null,
    image_type: null,
    show_border: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && badge) {
      setFormData({
        name: badge.name || '',
        color: badge.color || '#6366F1',
        icon_type: badge.icon_type || 'star',
        description: badge.description || '',
        image_data: null,
        image_type: null,
        show_border: badge.show_border !== false, // Default to true if not set
      });
      setImagePreview(badge.image_url || null);
    }
  }, [badge, isOpen]);
  
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setFormData(prev => ({ ...prev, image_data: base64, image_type: file.type }));
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveImage = async () => {
    if (badge.image_url && !formData.image_data) {
      // Remove from server
      try {
        await adminApi.delete(`/api/admin/system-badges/${badge.level}/image`);
        toast.success('Custom image removed');
      } catch (error) {
        toast.error('Failed to remove image');
        return;
      }
    }
    setFormData(prev => ({ ...prev, image_data: null, image_type: null }));
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave(badge.level, formData);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };
  
  const Icon = ICON_MAP[formData.icon_type] || Star;
  
  if (!isOpen || !badge) return null;
  
  const iconOptions = [
    { value: 'crown', label: 'Crown' },
    { value: 'shield', label: 'Shield' },
    { value: 'briefcase', label: 'Briefcase' },
    { value: 'award', label: 'Award' },
    { value: 'target', label: 'Target' },
    { value: 'star', label: 'Star' },
    { value: 'zap', label: 'Lightning' },
    { value: 'sparkles', label: 'Sparkles' },
    { value: 'heart', label: 'Heart' },
  ];
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/40">
          <div>
            <h2 className="text-lg font-semibold text-[#1a1a2e]">Edit Designation Badge</h2>
            <p className="text-sm text-neutral-500">Level: {badge.level} (Rank {badge.rank})</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {/* Preview */}
          <div className="flex justify-center py-4 bg-white/40 rounded-xl">
            <div className="text-center">
              {imagePreview ? (
                <div 
                  className={`w-12 h-12 rounded-full overflow-hidden mx-auto shadow ${formData.show_border ? 'border-2' : ''}`}
                  style={{ borderColor: formData.show_border ? formData.color : 'transparent' }}
                >
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center mx-auto shadow"
                  style={{ backgroundColor: formData.color }}
                >
                  <Icon size={20} className="text-white" />
                </div>
              )}
              <p className="mt-2 font-medium text-[#1a1a2e] text-sm">{formData.name}</p>
            </div>
          </div>
          
          {/* Custom Image Upload */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Custom Icon Image</label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="flex-1 px-2 py-1.5 border border-white/40 rounded-xl text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700"
              />
              {imagePreview && (
                <button type="button" onClick={handleRemoveImage} className="p-1.5 text-neutral-400 hover:text-red-500 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-neutral-400 mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Recommended: Square image (1:1), PNG/JPG, max 1MB. Auto-resized to 256×256px.
            </p>
          </div>
          
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Display Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          
          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-10 h-8 rounded cursor-pointer border border-white/40"
              />
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="flex-1 px-3 py-2 border border-white/40 rounded-xl text-sm font-mono"
              />
            </div>
          </div>
          
          {/* Icon (only if no custom image) */}
          {!imagePreview && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Default Icon</label>
              <div className="grid grid-cols-5 gap-1.5">
                {iconOptions.map((icon) => {
                  const IconComponent = ICON_MAP[icon.value] || Star;
                  const isSelected = formData.icon_type === icon.value;
                  return (
                    <button
                      key={icon.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icon_type: icon.value }))}
                      className={`p-2 rounded-xl border transition-all ${
                        isSelected ? 'border-teal-500 bg-teal-50' : 'border-white/40 hover:border-white/40'
                      }`}
                      title={icon.label}
                    >
                      <IconComponent className="w-4 h-4 mx-auto" style={{ color: isSelected ? '#14B8A6' : formData.color }} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Show Border Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Show Circle Border</label>
              <p className="text-xs text-neutral-500">Display a colored border around the badge</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, show_border: !prev.show_border }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.show_border ? 'bg-teal-600' : 'bg-gray-300'
              }`}
              data-testid="designation-badge-border-toggle"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                  formData.show_border ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/40 bg-white/40">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 text-sm text-neutral-700 font-medium rounded-xl hover:bg-neutral-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Assign Badge Modal
function AssignBadgeModal({ isOpen, onClose, badge, employees, onAssign }) {
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [reason, setReason] = useState('');
  const [awardedFor, setAwardedFor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [assigning, setAssigning] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setSelectedEmployees([]);
      setReason('');
      setAwardedFor('');
      setSearchTerm('');
    }
  }, [isOpen]);
  
  const filteredEmployees = employees?.filter(emp => {
    const name = emp.personal?.full_name || emp.name || '';
    const email = emp.work_email || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) || 
           email.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];
  
  const toggleEmployee = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };
  
  const handleAssign = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    setAssigning(true);
    try {
      await onAssign({
        badge_id: badge._id,
        employee_ids: selectedEmployees,
        reason,
        awarded_for: awardedFor
      });
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setAssigning(false);
    }
  };
  
  if (!isOpen || !badge) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/40">
          <BadgePreview badge={badge} size="small" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-[#1a1a2e]">Assign &quot;{badge.name}&quot;</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Award Title</label>
            <input
              type="text"
              value={awardedFor}
              onChange={(e) => setAwardedFor(e.target.value)}
              placeholder="e.g., Employee of the Month - March 2024"
              className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              data-testid="awarded-for-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this badge being awarded?"
              rows={2}
              className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Select Employees <span className="text-teal-600">({selectedEmployees.length})</span>
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            
            <div className="border border-white/40 rounded-xl max-h-48 overflow-y-auto">
              {filteredEmployees.map((emp) => {
                const empId = emp._id;
                const name = emp.personal?.full_name || emp.name || 'Unknown';
                const isSelected = selectedEmployees.includes(empId);
                
                return (
                  <div
                    key={empId}
                    onClick={() => toggleEmployee(empId)}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-white/30 last:border-0 ${
                      isSelected ? 'bg-teal-50' : 'hover:bg-white/60'
                    }`}
                    data-testid={`employee-select-${empId}`}
                  >
                    <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                      isSelected ? 'bg-teal-500 border-teal-500' : 'border-white/40'
                    }`}>
                      {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1a1a2e] truncate">{name}</p>
                    </div>
                  </div>
                );
              })}
              
              {filteredEmployees.length === 0 && (
                <p className="text-center text-neutral-500 py-6 text-sm">No employees found</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/40 bg-white/40">
          <button onClick={onClose} disabled={assigning} className="px-3 py-2 text-sm text-neutral-700 font-medium rounded-xl hover:bg-neutral-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={assigning || selectedEmployees.length === 0}
            className="px-4 py-2 text-sm bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-1"
            data-testid="assign-badge-btn"
          >
            {assigning && <Loader2 className="w-3 h-3 animate-spin" />}
            Assign ({selectedEmployees.length})
          </button>
        </div>
      </div>
    </div>
  );
}

// View Badge Details Modal
function ViewBadgeModal({ isOpen, onClose, badge, onRevoke }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && badge) {
      fetchDetails();
    }
  }, [isOpen, badge]);
  
  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`/api/admin/badges/${badge._id}`);
      setDetails(response.data);
    } catch (error) {
      toast.error('Failed to load badge details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRevoke = async (employeeId) => {
    if (!window.confirm('Revoke this badge?')) return;
    
    try {
      await onRevoke(badge._id, employeeId);
      fetchDetails();
      toast.success('Badge revoked');
    } catch (error) {
      // Error handled in parent
    }
  };
  
  if (!isOpen || !badge) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/40">
          <BadgePreview badge={badge} size="medium" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-[#1a1a2e]">{badge.name}</h2>
            <p className="text-sm text-neutral-500">{badge.description || 'No description'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <h3 className="text-sm font-medium text-[#1a1a2e] mb-2">
            Awarded to {details?.total_assignments || 0} employee{details?.total_assignments !== 1 ? 's' : ''}
          </h3>
          
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-12 bg-neutral-100 rounded animate-pulse" />)}
            </div>
          ) : (
            <div className="border border-white/40 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/40">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600">Employee</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600">Date</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {details?.assignments?.map((a) => (
                    <tr key={a._id}>
                      <td className="px-3 py-2 font-medium text-[#1a1a2e]">{a.employee_name}</td>
                      <td className="px-3 py-2 text-neutral-500">{formatDate(a.awarded_at)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => handleRevoke(a.employee_id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  
                  {(!details?.assignments || details.assignments.length === 0) && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-neutral-500">Not awarded yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-white/40 bg-white/40">
          <button onClick={onClose} className="px-3 py-2 text-sm text-neutral-700 font-medium rounded-xl hover:bg-neutral-100 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// System Badges Section with actual TenureBadge and editable Designation badges
function SystemBadgesSection({ designationBadges, onEditDesignation, onRefresh }) {
  const [activeTab, setActiveTab] = useState('tenure');
  
  // Sample years for tenure preview (showing the actual badge design)
  const tenureYears = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15];
  
  return (
    <div className="glass-card shadow-soft overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/40">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="font-semibold text-[#1a1a2e] text-sm">System Badges</h3>
            <p className="text-xs text-neutral-500">Auto-awarded based on tenure & designation</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-white/40">
        <button
          onClick={() => setActiveTab('tenure')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
            activeTab === 'tenure' ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50/50' : 'text-neutral-600 hover:text-[#1a1a2e]'
          }`}
        >
          <Clock className="w-3.5 h-3.5" />
          Tenure Badges
        </button>
        <button
          onClick={() => setActiveTab('designation')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
            activeTab === 'designation' ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50/50' : 'text-neutral-600 hover:text-[#1a1a2e]'
          }`}
        >
          <Briefcase className="w-3.5 h-3.5" />
          Designation Badges
        </button>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {activeTab === 'tenure' && (
          <div>
            <div className="flex flex-wrap justify-center gap-3">
              {tenureYears.map((years) => (
                <TenureBadge key={years} years={years} size="small" showLabel={true} animate={false} />
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-3 text-center">
              Actual badges shown to employees based on their tenure
            </p>
          </div>
        )}
        
        {activeTab === 'designation' && (
          <div>
            <div className="flex flex-wrap justify-center gap-4">
              {designationBadges.map((badge) => (
                <DesignationBadgePreview 
                  key={badge.level}
                  badge={badge}
                  onEdit={onEditDesignation}
                />
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-3 text-center">
              Click on any badge to customize with your own 3D icons
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Badge Card
function BadgeCard({ badge, onEdit, onDelete, onAssign, onView }) {
  const [showMenu, setShowMenu] = useState(false);
  
  return (
    <div className="glass-card p-3 hover:shadow-soft transition-shadow" data-testid={`badge-card-${badge._id}`}>
      <div className="flex items-start gap-3">
        <BadgePreview badge={badge} size="medium" />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-[#1a1a2e] text-sm truncate">{badge.name}</h3>
              <p className="text-xs text-neutral-500 line-clamp-1 mt-0.5">{badge.description || 'No description'}</p>
            </div>
            
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-7 w-40 glass-card rounded-xl shadow-lg z-20 py-1">
                    <button onClick={() => { onView(badge); setShowMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs text-neutral-700 hover:bg-white/60 flex items-center gap-2">
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <button onClick={() => { onAssign(badge); setShowMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs text-neutral-700 hover:bg-white/60 flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5" /> Assign
                    </button>
                    <button onClick={() => { onEdit(badge); setShowMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs text-neutral-700 hover:bg-white/60 flex items-center gap-2">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => { onDelete(badge); setShowMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 flex items-center gap-2">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded">{badge.category || 'recognition'}</span>
            <span className="text-xs text-neutral-400 flex items-center gap-0.5">
              <Users className="w-3 h-3" />
              {badge.unique_employees || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Badge Recognition Notice Prompt Modal
function NoticePromptModal({ isOpen, onClose, noticeSuggestion, onPublish, onSkip, publishing }) {
  const [customMessage, setCustomMessage] = useState('');
  
  useEffect(() => {
    if (noticeSuggestion) {
      setCustomMessage(noticeSuggestion.suggested_message || '');
    }
  }, [noticeSuggestion]);
  
  if (!isOpen || !noticeSuggestion) return null;
  
  const badge = noticeSuggestion.badge_details || {};
  const employees = noticeSuggestion.employees || [];
  const Icon = ICON_MAP[badge.icon] || Trophy;
  
  const handlePublish = () => {
    onPublish({
      ...noticeSuggestion,
      suggested_message: customMessage
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white  w-full max-w-lg shadow-2xl" data-testid="notice-prompt-modal">
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-white/30">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full p-3 shadow-lg">
            <Award className="w-6 h-6 text-white" />
          </div>
          <button 
            onClick={onSkip} 
            className="absolute top-4 right-4 p-1.5 hover:bg-neutral-100 rounded-full text-neutral-400 hover:text-neutral-600"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-bold text-[#1a1a2e] text-center mt-4">Publish Achievement Notice?</h2>
          <p className="text-sm text-neutral-500 text-center mt-1">Share this accomplishment with the whole team</p>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Badge Preview Card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl p-4">
            <div className="flex items-center gap-4">
              {/* Badge Icon/Image */}
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center shadow-soft flex-shrink-0 border-2"
                style={{ 
                  backgroundColor: badge.color ? `${badge.color}15` : '#FEF3C7',
                  borderColor: badge.color || '#F59E0B'
                }}
              >
                {badge.image_url ? (
                  <img src={badge.image_url} alt={badge.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <Icon className="w-8 h-8" style={{ color: badge.color || '#F59E0B' }} />
                )}
              </div>
              
              {/* Badge Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[#1a1a2e]">{badge.name || 'Achievement Badge'}</h3>
                <p className="text-sm text-neutral-600 mt-0.5">
                  Awarded to {employees.length === 1 
                    ? employees[0].name 
                    : `${employees.length} team members`}
                </p>
                {employees.length <= 3 && employees.length > 1 && (
                  <p className="text-xs text-neutral-500 mt-1 truncate">
                    {employees.map(e => e.name).join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Editable Message */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Notice Message
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              className="w-full px-4 py-3 border border-white/40 rounded-xl resize-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-neutral-700 text-sm"
              rows={3}
              placeholder="Write a message to accompany this badge recognition..."
              data-testid="notice-message-input"
            />
            <p className="text-xs text-neutral-400 mt-1.5 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              This notice will be visible on the company board for 3 days
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-3 p-6 pt-2 border-t border-white/30 bg-white/40 rounded-b-2xl">
          <button
            onClick={onSkip}
            className="flex-1 px-4 py-2.5 border border-white/40 text-neutral-600 rounded-xl hover:bg-white/60 font-medium text-sm transition-colors"
            data-testid="notice-skip-btn"
          >
            Skip
          </button>
          <button
            onClick={handlePublish}
            disabled={publishing || !customMessage.trim()}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="notice-publish-btn"
          >
            {publishing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Publishing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Publish Notice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function AdminBadgeManagement() {
  const [badges, setBadges] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [designationBadges, setDesignationBadges] = useState([]);
  const [badgeSettings, setBadgeSettings] = useState({
    show_tenure_badges: true,
    show_designation_badges: true,
    show_custom_badges: true
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningBadge, setAssigningBadge] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingBadge, setViewingBadge] = useState(null);
  const [designationModalOpen, setDesignationModalOpen] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState(null);
  
  // Notice prompt state (for badge-to-notice integration)
  const [noticePromptOpen, setNoticePromptOpen] = useState(false);
  const [noticeSuggestion, setNoticeSuggestion] = useState(null);
  const [publishingNotice, setPublishingNotice] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [badgesRes, metadataRes, employeesRes, systemBadgesRes, settingsRes] = await Promise.all([
        adminApi.get('/api/admin/badges?include_inactive=true'),
        adminApi.get('/api/admin/badges/metadata'),
        adminApi.get('/api/admin/employees'),
        adminApi.get('/api/admin/system-badges'),
        adminApi.get('/api/admin/badge-settings')
      ]);
      
      setBadges(badgesRes.data.badges || []);
      setMetadata(metadataRes.data);
      setEmployees(employeesRes.data || []);
      setDesignationBadges(systemBadgesRes.data.system_badges || []);
      setBadgeSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching badge data:', error);
      toast.error('Failed to load badge data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleBadgeSetting = async (key) => {
    const newValue = !badgeSettings[key];
    try {
      await adminApi.put('/api/admin/badge-settings', { [key]: newValue });
      setBadgeSettings(prev => ({ ...prev, [key]: newValue }));
      toast.success(`${key.replace(/_/g, ' ').replace('show ', '')} ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };
  
  const handleCreateBadge = async (formData) => {
    try {
      await adminApi.post('/api/admin/badges', formData);
      toast.success('Badge created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create badge');
      throw error;
    }
  };
  
  const handleUpdateBadge = async (formData) => {
    try {
      await adminApi.put(`/api/admin/badges/${editingBadge._id}`, formData);
      toast.success('Badge updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update badge');
      throw error;
    }
  };
  
  const handleDeleteBadge = async (badge) => {
    if (!window.confirm(`Delete "${badge.name}"?`)) return;
    
    try {
      await adminApi.delete(`/api/admin/badges/${badge._id}`);
      toast.success('Badge deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete badge');
    }
  };
  
  const handleAssignBadge = async (data) => {
    try {
      const response = await adminApi.post('/api/admin/badges/assign', data);
      toast.success(response.data.message);
      fetchData();
      
      // Show notice prompt if assignment was successful and notice_suggestion exists
      if (response.data.notice_suggestion && response.data.assigned_to?.length > 0) {
        setNoticeSuggestion(response.data.notice_suggestion);
        setNoticePromptOpen(true);
      }
      
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign badge');
      throw error;
    }
  };
  
  const handlePublishBadgeNotice = async () => {
    if (!noticeSuggestion) return;
    
    setPublishingNotice(true);
    try {
      const response = await adminApi.post('/api/notices/badge-recognition', {
        employee_ids: noticeSuggestion.employees.map(e => e.id),
        badge_id: noticeSuggestion.badge_details?.id,
        message: noticeSuggestion.suggested_message,
        badge_details: noticeSuggestion.badge_details
      });
      
      toast.success('Badge recognition notice published! Visible for 3 days.');
      setNoticePromptOpen(false);
      setNoticeSuggestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish notice');
    } finally {
      setPublishingNotice(false);
    }
  };
  
  const handleSkipNotice = () => {
    setNoticePromptOpen(false);
    setNoticeSuggestion(null);
  };
  
  const handleRevokeBadge = async (badgeId, employeeId) => {
    try {
      await adminApi.delete('/api/admin/badges/revoke', {
        data: { badge_id: badgeId, employee_id: employeeId }
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke badge');
      throw error;
    }
  };
  
  const handleUpdateDesignationBadge = async (level, formData) => {
    try {
      await adminApi.put(`/api/admin/system-badges/${level}`, formData);
      toast.success('Designation badge updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
      throw error;
    }
  };
  
  const filteredBadges = badges.filter(badge => {
    const matchesSearch = badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (badge.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || badge.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  
  const stats = {
    totalBadges: badges.length,
    activeBadges: badges.filter(b => b.is_active !== false).length,
    totalAssignments: badges.reduce((sum, b) => sum + (b.assignment_count || 0), 0),
    uniqueRecipients: badges.reduce((sum, b) => sum + (b.unique_employees || 0), 0),
  };
  
  if (loading) {
    return (
      <div className="space-y-4" data-testid="badge-management-loading">
        <div className="h-8 w-48 bg-neutral-200 rounded animate-pulse" />
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-neutral-200 rounded-xl animate-pulse" />)}
        </div>
        <div className="h-40 bg-neutral-200 rounded-xl animate-pulse" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="badge-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-[#1a1a2e] flex items-center gap-2">
            <Award className="w-5 h-5 text-teal-600" />
            Badge Management
          </h1>
          <p className="text-sm text-neutral-500">Create and assign recognition badges</p>
        </div>
        
        <button 
          onClick={() => { setEditingBadge(null); setFormModalOpen(true); }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors"
          data-testid="create-badge-btn"
        >
          <Plus className="w-4 h-4" />
          Create Badge
        </button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.totalBadges, icon: Award, color: 'bg-indigo-100 text-indigo-600' },
          { label: 'Active', value: stats.activeBadges, icon: Check, color: 'bg-green-100 text-green-600' },
          { label: 'Awards', value: stats.totalAssignments, icon: Trophy, color: 'bg-purple-100 text-purple-600' },
          { label: 'Recipients', value: stats.uniqueRecipients, icon: Users, color: 'bg-amber-100 text-amber-600' },
        ].map((stat) => (
          <div key={stat.label} className="glass-card px-3 py-2">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1a1a2e]">{stat.value}</p>
                <p className="text-xs text-neutral-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Badge Visibility Settings */}
      <div className="glass-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-4 h-4 text-neutral-500" />
          <h3 className="text-sm font-semibold text-[#1a1a2e]">Badge Visibility</h3>
          <span className="text-xs text-neutral-400">Control which badges are shown on employee profiles</span>
        </div>
        <div className="flex flex-wrap gap-4">
          {[
            { key: 'show_tenure_badges', label: 'Tenure Badges', icon: Clock },
            { key: 'show_designation_badges', label: 'Designation Badges', icon: Briefcase },
            { key: 'show_custom_badges', label: 'Custom Badges', icon: Award },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => handleToggleBadgeSetting(key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors ${
                badgeSettings[key] 
                  ? 'bg-teal-50 border-teal-200 text-teal-700' 
                  : 'bg-white/40 border-white/40 text-neutral-500'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{label}</span>
              {badgeSettings[key] ? (
                <ToggleRight className="w-5 h-5 text-teal-600" />
              ) : (
                <ToggleLeft className="w-5 h-5 text-neutral-400" />
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* System Badges */}
      <SystemBadgesSection 
        designationBadges={designationBadges}
        onEditDesignation={(badge) => { setEditingDesignation(badge); setDesignationModalOpen(true); }}
        onRefresh={fetchData}
      />
      
      {/* Custom Badges */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-[#1a1a2e]">Custom Badges</h2>
          <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">{badges.length}</span>
        </div>
        
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-40 pl-8 pr-3 py-1.5 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              data-testid="badge-search-input"
            />
          </div>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-2 py-1.5 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All</option>
            {metadata?.categories?.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Badge Grid */}
      {filteredBadges.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-white/40 py-12 text-center">
          <Award className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-[#1a1a2e] mb-1">
            {badges.length === 0 ? 'No badges yet' : 'No matches'}
          </h3>
          <p className="text-xs text-neutral-500 mb-3">
            {badges.length === 0 ? 'Create your first badge' : 'Try different filters'}
          </p>
          {badges.length === 0 && (
            <button 
              onClick={() => { setEditingBadge(null); setFormModalOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700"
            >
              <Plus className="w-3.5 h-3.5" />
              Create Badge
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredBadges.map((badge) => (
            <BadgeCard
              key={badge._id}
              badge={badge}
              onEdit={(b) => { setEditingBadge(b); setFormModalOpen(true); }}
              onDelete={handleDeleteBadge}
              onAssign={(b) => { setAssigningBadge(b); setAssignModalOpen(true); }}
              onView={(b) => { setViewingBadge(b); setViewModalOpen(true); }}
            />
          ))}
        </div>
      )}
      
      {/* Modals */}
      <BadgeFormModal
        isOpen={formModalOpen}
        onClose={() => { setFormModalOpen(false); setEditingBadge(null); }}
        badge={editingBadge}
        metadata={metadata}
        onSave={editingBadge ? handleUpdateBadge : handleCreateBadge}
      />
      
      <AssignBadgeModal
        isOpen={assignModalOpen}
        onClose={() => { setAssignModalOpen(false); setAssigningBadge(null); }}
        badge={assigningBadge}
        employees={employees}
        onAssign={handleAssignBadge}
      />
      
      <ViewBadgeModal
        isOpen={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setViewingBadge(null); }}
        badge={viewingBadge}
        onRevoke={handleRevokeBadge}
      />
      
      <EditDesignationBadgeModal
        isOpen={designationModalOpen}
        onClose={() => { setDesignationModalOpen(false); setEditingDesignation(null); }}
        badge={editingDesignation}
        onSave={handleUpdateDesignationBadge}
      />
      
      {/* Badge Recognition Notice Prompt */}
      <NoticePromptModal
        isOpen={noticePromptOpen}
        onClose={handleSkipNotice}
        noticeSuggestion={noticeSuggestion}
        onPublish={(data) => {
          // Update the suggestion message before publishing
          setNoticeSuggestion(prev => ({ ...prev, suggested_message: data.suggested_message }));
          handlePublishBadgeNotice();
        }}
        onSkip={handleSkipNotice}
        publishing={publishingNotice}
      />
    </div>
  );
}
